import { Card, Input, Space, Tag } from 'antd';
import React from 'react';

import Text from '@/atoms/Text';
import { HealthIndicator } from '@/components/common/HealthIndicator/HealthIndicator';
import type { HealthColor } from '@/components/common/HealthIndicator/HealthIndicator';

export interface HealthIndicatorsCardProps {
  onSchedule?: string;
  onBudget?: string;
  csat?: string;
  teamHealth?: string;
  notes?: string;
  onHealthChange?: (health: {
    onSchedule?: string;
    onBudget?: string;
    csat?: string;
    teamHealth?: string;
    notes?: string;
  }) => void;
  disabled?: boolean;
}

const STATUS_LABELS: Record<HealthColor, string> = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
};

interface HealthStatusSelectorProps {
  value?: HealthColor;
  onChange: (value: HealthColor) => void;
  isDisabled?: boolean;
}

const HealthStatusSelector: React.FC<HealthStatusSelectorProps> = ({
  value,
  onChange,
  isDisabled,
}) => (
  <div className="health-indicators-card__status-row">
    {(['green', 'yellow', 'red'] as const).map((status) => {
      const activeClass = value === status ? 'health-indicators-card__status-btn--active' : '';
      const disabledClass = isDisabled ? 'health-indicators-card__status-btn--disabled' : '';
      return (
        <button
          key={status}
          className={`health-indicators-card__status-btn health-indicators-card__status-btn--${status} ${activeClass} ${disabledClass}`}
          onClick={() => !isDisabled && onChange(status)}
          aria-pressed={value === status}
          disabled={isDisabled}
        >
          <HealthIndicator status={status} hideGlyph />
          <span className="health-indicators-card__status-label">{STATUS_LABELS[status]}</span>
        </button>
      );
    })}
  </div>
);

export function HealthIndicatorsCard({
  onSchedule,
  onBudget,
  csat,
  teamHealth,
  notes = '',
  onHealthChange,
  disabled = false,
}: Readonly<HealthIndicatorsCardProps>): React.ReactElement {
  const handleScheduleChange = (value: HealthColor) => {
    onHealthChange?.({ onSchedule: value, onBudget, csat, teamHealth, notes });
  };

  const handleOnBudgetChange = (value: HealthColor) => {
    onHealthChange?.({ onSchedule, onBudget: value, csat, teamHealth, notes });
  };

  const handleCSATChange = (value: HealthColor) => {
    onHealthChange?.({ onSchedule, onBudget, csat: value, teamHealth, notes });
  };

  const handleTeamHealthChange = (value: HealthColor) => {
    onHealthChange?.({ onSchedule, onBudget, csat, teamHealth: value, notes });
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onHealthChange?.({ onSchedule, onBudget, csat, teamHealth, notes: e.target.value });
  };

  return (
    <Card variant="borderless" className="health-indicators-card">
      <Space direction="vertical" size="large" className="health-indicators-card__full-width">
        <div className="card__header">
          <Text variant="h5">Health Indicators</Text>
          <Tag className="card__source-tag">Source: Manual</Tag>
        </div>

        {/* On Schedule */}
        <div className="health-indicators-card__health-row">
          <Text variant="bodyDefault">On Schedule</Text>
          <HealthStatusSelector
            value={onSchedule as HealthColor | undefined}
            onChange={handleScheduleChange}
            isDisabled={disabled}
          />
        </div>

        {/* On Budget */}
        <div className="health-indicators-card__health-row">
          <Text variant="bodyDefault">On Budget</Text>
          <HealthStatusSelector
            value={onBudget as HealthColor | undefined}
            onChange={handleOnBudgetChange}
            isDisabled={disabled}
          />
        </div>

        {/* CSAT */}
        <div className="health-indicators-card__health-row">
          <Text variant="bodyDefault">CSAT</Text>
          <HealthStatusSelector
            value={csat as HealthColor | undefined}
            onChange={handleCSATChange}
            isDisabled={disabled}
          />
        </div>

        {/* Team Health */}
        <div className="health-indicators-card__health-row">
          <Text variant="bodyDefault">Team Health</Text>
          <HealthStatusSelector
            value={teamHealth as HealthColor | undefined}
            onChange={handleTeamHealthChange}
            isDisabled={disabled}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="health-indicators-card__label">
            <Text variant="bodyDefault">
              NOTES <span className="health-indicators-card__label-required">*</span>
            </Text>
          </label>
          <Input.TextArea
            rows={3}
            placeholder="Summarize the current project status."
            className="health-indicators-card__textarea"
            value={notes}
            onChange={handleNotesChange}
            disabled={disabled}
          />
        </div>
        <Text variant="caption" color="var(--text-secondary)">
          Required for every weekly update
        </Text>
      </Space>
    </Card>
  );
}
